import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { exportSystemConfig, importSystemConfig } from "../api/adminApi";
import { Hero } from "../components/adminSurface/AdminSurface";
import { LayersArt } from "../components/adminSurface/heroArt";

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconExport() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 1v8M5 6l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function IconImport() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 9V1M5 4l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Alert ─────────────────────────────────────────────────────────────────────

function Alert({ type = "info", children, onClose }) {
  const palette = {
    error:   { bg: "#ffe9e9", text: "#8b0000",  border: "#f3c6c6" },
    success: { bg: "#e9f7ef", text: "#116622",  border: "#b8e6c8" },
    info:    { bg: "#f6f8ff", text: "#264b9a",  border: "#d0dff5" },
  }[type] ?? {};
  return (
    <div style={{
      background: palette.bg, color: palette.text,
      border: `1px solid ${palette.border}`,
      borderRadius: 8, padding: "10px 14px",
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      marginBottom: 16, gap: 12,
    }}>
      <span style={{ fontWeight: 600, lineHeight: 1.5 }}>{children}</span>
      {onClose && (
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: palette.text, flexShrink: 0 }}>✕</button>
      )}
    </div>
  );
}

// ── Summary card ──────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub }) {
  return (
    <div style={{
      background: "var(--card-bg, #fff)", border: "1px solid var(--border, #e5e7eb)",
      borderRadius: 10, padding: "16px 20px", minWidth: 120,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "var(--text, #111)" }}>{value}</div>
      {sub != null && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Import result row ─────────────────────────────────────────────────────────

function ResultRow({ label, added, skipped, saved }) {
  if (added == null && skipped == null && saved == null) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
      <span style={{ flex: 1, fontSize: 13, color: "#374151" }}>{label}</span>
      {saved != null && <span style={{ fontSize: 13, fontWeight: 600, color: "#0284c7" }}>{saved} saved</span>}
      {added != null && <span style={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}>+{added} added</span>}
      {skipped != null && <span style={{ fontSize: 13, color: "#9ca3af" }}>{skipped} skipped</span>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SystemConfigPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const importFileRef = useRef(null);

  const [preview, setPreview] = useState(null);   // parsed snapshot waiting for confirm
  const [previewFile, setPreviewFile] = useState(null); // filename
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);     // import result
  const [msg, setMsg] = useState(null);           // { type, text }

  // ── Export ────────────────────────────────────────────────────────────────

  async function handleExport() {
    setExporting(true);
    setMsg(null);
    try {
      const snapshot = await exportSystemConfig(token);
      const json = JSON.stringify(snapshot, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `system.depconfig.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg({ type: "error", text: e?.message || t("admin.systemConfigExportError", { defaultValue: "Export failed." }) });
    } finally {
      setExporting(false);
    }
  }

  // ── File pick → show preview ──────────────────────────────────────────────

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setMsg(null);
    setResult(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      setPreview(parsed);
      setPreviewFile(file.name);
    } catch {
      setMsg({ type: "error", text: t("admin.importConfigInvalidJson", { defaultValue: "Invalid JSON file." }) });
    }
  }

  // ── Confirm import ────────────────────────────────────────────────────────

  async function handleConfirmImport() {
    if (!preview) return;
    setImporting(true);
    setMsg(null);
    try {
      const res = await importSystemConfig(token, preview);
      setResult(res);
      setPreview(null);
      setPreviewFile(null);
      setMsg({ type: "success", text: t("admin.systemConfigImportSuccess", { defaultValue: "Import complete." }) });
    } catch (e) {
      setMsg({ type: "error", text: e?.message || t("admin.systemConfigImportError", { defaultValue: "Import failed." }) });
    } finally {
      setImporting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const dbCount     = preview?.databases?.length ?? 0;
  const wfCount     = preview?.workflows?.length ?? 0;
  const ingestCount = preview?.definitions?.ingestFileTypes?.length ?? 0;
  const outputCount = preview?.definitions?.outputFileTypes?.length ?? 0;
  const convCount   = preview?.definitions?.conversionApis?.length ?? 0;

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />

      <div className="page-container">
        <div className="indexes-page">

          {/* ── Header ── */}
          <Hero
            title={t("admin.systemConfig", { defaultValue: "System Configuration" })}
            subtitle={t("admin.systemConfigSubtitle", {
              defaultValue: "Export and import database definitions, system definitions, and workflows as a single file.",
            })}
            logo={<LayersArt />}
            actions={
              <>
                <button
                  type="button"
                  className="asf-hero__btn"
                  onClick={handleExport}
                  disabled={exporting}
                  title={t("admin.exportSystemConfig", { defaultValue: "Export All" })}
                >
                  <IconExport /> {t("admin.exportSystemConfig", { defaultValue: "Export All" })}
                </button>
                <button
                  type="button"
                  className="asf-hero__btn"
                  onClick={() => importFileRef.current?.click()}
                  title={t("admin.importSystemConfig", { defaultValue: "Import" })}
                >
                  <IconImport /> {t("admin.importSystemConfig", { defaultValue: "Import" })}
                </button>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".json,.depconfig.json"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </>
            }
          />

          {msg && (
            <Alert type={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>
          )}

          {/* ── Preview / confirm ── */}
          {preview && (
            <div style={{
              background: "var(--card-bg, #fff)", border: "1px solid var(--border, #e5e7eb)",
              borderRadius: 12, padding: 20, marginBottom: 20,
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: "#111" }}>
                {t("admin.systemConfigPreviewTitle", { defaultValue: "File Preview" })}
                {previewFile && <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>— {previewFile}</span>}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
                <SummaryCard label={t("admin.systemConfigDatabases", { defaultValue: "Databases" })} value={dbCount} />
                <SummaryCard label={t("admin.systemConfigWorkflows", { defaultValue: "Workflows" })} value={wfCount} />
                <SummaryCard
                  label={t("admin.systemConfigDefinitions", { defaultValue: "Definitions" })}
                  value={ingestCount + outputCount + convCount}
                  sub={`${ingestCount} ingest · ${outputCount} output · ${convCount} APIs`}
                />
              </div>

              {preview.exportedAt && (
                <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
                  Exported: {new Date(preview.exportedAt).toLocaleString()}
                  {preview.organization && ` · Org: ${preview.organization}`}
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={importing}
                  onClick={handleConfirmImport}
                >
                  {importing
                    ? t("common.saving", { defaultValue: "Saving…" })
                    : t("admin.systemConfigConfirmImport", { defaultValue: "Confirm Import" })}
                </button>
                <button
                  type="button"
                  className="dbModalCancel"
                  onClick={() => { setPreview(null); setPreviewFile(null); }}
                  disabled={importing}
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
              </div>
            </div>
          )}

          {/* ── Last import result ── */}
          {result && (
            <div style={{
              background: "var(--card-bg, #fff)", border: "1px solid var(--border, #e5e7eb)",
              borderRadius: 12, padding: 20, marginBottom: 20,
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: "#111" }}>
                {t("admin.systemConfigImportResult", { defaultValue: "Import Results" })}
              </div>
              <ResultRow
                label={t("admin.systemConfigDatabases", { defaultValue: "Databases" })}
                added={result.dbsUpdated}
                skipped={result.dbsSkipped}
              />
              <ResultRow
                label="Indexes"
                added={result.indexesAdded}
                skipped={result.indexesSkipped}
              />
              <ResultRow
                label="Ingest File Types"
                added={result.ingestAdded}
                skipped={result.ingestSkipped}
              />
              <ResultRow
                label="Output File Types"
                added={result.outputAdded}
                skipped={result.outputSkipped}
              />
              <ResultRow
                label="Conversion APIs"
                added={result.convAdded}
                skipped={result.convSkipped}
              />
              <ResultRow
                label={t("admin.systemConfigWorkflows", { defaultValue: "Workflows" })}
                saved={result.workflowsSaved}
              />
            </div>
          )}

          {/* ── Info card ── */}
          {!preview && !result && (
            <div style={{
              background: "var(--card-bg, #fff)", border: "1px solid var(--border, #e5e7eb)",
              borderRadius: 12, padding: 24,
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: "#111" }}>
                {t("admin.systemConfigWhatIsIncluded", { defaultValue: "What is included in the export?" })}
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, color: "#374151", fontSize: 13, lineHeight: 1.8 }}>
                <li>
                  <strong>{t("admin.systemConfigDatabases", { defaultValue: "Databases" })}</strong>
                  {" — "}
                  {t("admin.systemConfigDatabasesDesc", {
                    defaultValue: "All document databases with their field index definitions and configuration properties.",
                  })}
                </li>
                <li>
                  <strong>{t("admin.systemConfigDefinitions", { defaultValue: "Definitions" })}</strong>
                  {" — "}
                  {t("admin.systemConfigDefinitionsDesc", {
                    defaultValue: "Ingest file types, output file types, and conversion API mappings.",
                  })}
                </li>
                <li>
                  <strong>{t("admin.systemConfigWorkflows", { defaultValue: "Workflows" })}</strong>
                  {" — "}
                  {t("admin.systemConfigWorkflowsDesc", {
                    defaultValue: "All active flow definitions configured for your organization.",
                  })}
                </li>
              </ul>
              <div style={{ marginTop: 16, padding: "10px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 12, color: "#92400e" }}>
                {t("admin.systemConfigImportNote", {
                  defaultValue: "On import: databases are merged into existing ones only (new databases are skipped). Definitions are skipped if the TypeId already exists. Workflows are upserted by name.",
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
